import { useState } from "react";
import { firebaseLogin } from "../api/firebaseAuth";
import { validateUser } from "../api/authApi";
import { useAuth } from "../context/AuthContext";
import { deriveCustomerNameFromEmail } from "../utils/auth";
import PersonaSelectorModal from './PersonaSelectorModal';
import SignUpPage from './Login/SignUpPage';

export default function SignInModal({ onClose }) {
  const { login } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPersonaModal, setShowPersonaModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // 1. Firebase — get ID token.
      const token = await firebaseLogin(username, password);

      // 2. Backend validation — sets auth cookies, returns user data.
      const userData = await validateUser(token);

      const customerEmail = userData.email || username;
      const customerName = deriveCustomerNameFromEmail(
        customerEmail,
        userData.customer_name
      );

      // 3. Build the profile shape AuthContext expects.
      const profile =
        userData.role === "A"
          ? {
              admin_username: userData.admin_username,
              role: "A",
              users: userData.users || [],
            }
          : {
              customer_id: userData.user_id,
              customer_name: customerName,
              username: customerEmail,
              role: "C",
            };

      // 4. Hand off to AuthContext:
      //    - updates useAuth() state (all consumers re-render)
      //    - awaits WebSocket open
      //    - dispatches "authStateChanged" so ChatBot triggers avatar
      await login(profile);

      onClose();

      // Navigate to any pending product page without a hard reload.
      const pendingProductId = sessionStorage.getItem("pendingProductId");
      if (pendingProductId) {
        sessionStorage.removeItem("pendingProductId");
        window.location.href = `/item/${pendingProductId}`;
      }
    } catch (err) {
      setError(err.message || "Sign in failed. Please try again.");
      setIsLoading(false);
    }
  };

  const handleSelectPersona = (persona) => {
    setUsername(persona.username);
    setPassword(persona.password);
    setShowPersonaModal(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white w-full max-w-sm p-6 rounded-lg shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">SIGN IN</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 text-xl"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <p className="text-sm text-gray-700 mb-4">
          Sign in with your username to access your account.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={`w-full px-3 py-2 border ${error ? "border-red-500" : "border-gray-300"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-red-500`}
              required
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`w-full px-3 py-2 border ${error ? "border-red-500" : "border-gray-300"
                } rounded-md focus:outline-none focus:ring-2 focus:ring-red-500`}
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 bg-black text-white font-semibold rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {isLoading ? "Signing in..." : "SIGN IN"}
          </button>
        </form>

        <div className="mt-4 text-sm text-center text-gray-500">
          <button
            type="button"
            onClick={() => setShowSignUpModal(true)}
            className="text-blue-600 hover:underline font-medium"
          >
            Sign up
          </button>{" "}
          to explore all features.
        </div>

        <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          All data is kept secure
        </div>
        <div className="mt-4 flex items-center justify-between w-full gap-2 text-sm text-gray-600">
          <button
            type="button"
            onClick={() => setShowPersonaModal(true)}
            className="text-xs text-blue-600 hover:underline font-medium"
          >
            DEMO LOGIN
          </button>

          <button
            type="button"
            onClick={() => {
              setUsername("admin");
              setPassword("admin");
            }}
            className="text-xs text-blue-600 hover:underline font-medium"
          >
            ADMIN LOGIN
          </button>
        </div>
      </div>

      {showPersonaModal && (
        <PersonaSelectorModal
          onClose={() => setShowPersonaModal(false)}
          onSelectPersona={handleSelectPersona}
        />
      )}

      {showSignUpModal && (
        <SignUpPage
          onClose={() => setShowSignUpModal(false)}
          onSignUpSuccess={() => setShowSignUpModal(false)}
        />
      )}
    </div>
  );
}